<template>
  <div class="hero-banner-video">
    <video
      ref="videoEl"
      class="hero-banner-video__el"
      :class="fit === 'contain' ? 'object-contain' : 'object-cover'"
      :src="activeSrc || undefined"
      autoplay
      muted
      loop
      playsinline
      preload="metadata"
      :aria-label="alt"
      @loadedmetadata="onLoadedMetadata"
    />
  </div>
</template>

<script setup lang="ts">
const props = withDefaults(defineProps<{
  desktopVideo?: string | null
  mobileVideo?: string | null
  alt: string
  fit?: 'cover' | 'contain'
}>(), {
  fit: 'cover',
})

const emit = defineEmits<{
  load: [dimensions: { width: number; height: number }]
}>()

const { mediaUrl } = useMediaUrl()
const videoEl = ref<HTMLVideoElement | null>(null)
const isMobileViewport = ref(false)

const activeSrc = computed(() => {
  const url = isMobileViewport.value
    ? (props.mobileVideo || props.desktopVideo)
    : (props.desktopVideo || props.mobileVideo)
  return url ? mediaUrl(url) : ''
})

function onLoadedMetadata() {
  const el = videoEl.value
  if (!el || el.videoWidth <= 0 || el.videoHeight <= 0) return
  emit('load', {
    width: el.videoWidth,
    height: el.videoHeight,
  })
}

function syncPlayback() {
  void videoEl.value?.play()?.catch(() => undefined)
}

onMounted(() => {
  const mq = window.matchMedia('(max-width: 767px)')
  const update = () => {
    isMobileViewport.value = mq.matches
  }
  update()
  mq.addEventListener('change', update)
  syncPlayback()

  onUnmounted(() => {
    mq.removeEventListener('change', update)
  })
})

watch(activeSrc, () => {
  nextTick(() => syncPlayback())
})
</script>

<style scoped>
.hero-banner-video {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
}

.hero-banner-video__el {
  display: block;
  width: 100%;
  height: 100%;
}
</style>
